import React, { useState, useEffect } from 'react';
import { FaEdit, FaPlus } from 'react-icons/fa';
import axiosPost from '../../../helpers/axios-helper';
import ArticleEditor from '../../../components/editor/ArticleEditor';
import '../../dashboard/styles/Dashboard.css';

export default function CountryProfiles() {
  const [profiles, setProfiles] = useState([]);
  const [countries, setCountries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editorData, setEditorData] = useState(null);
  const [selectedCountryId, setSelectedCountryId] = useState('');

  useEffect(() => {
    loadCountries();
    loadProfiles();
  }, []);

  const loadCountries = () => {
    axiosPost(
      'countries/list',
      { 'Content-Type': 'application/json' },
      {},
      (resp) => {
        if (resp?.success && resp?.data) {
          setCountries(resp.data);
        }
      },
      (err) => console.error('Failed to load countries:', err)
    );
  };

  const loadProfiles = () => {
    setIsLoading(true);
    axiosPost(
      'country-profiles/list',
      { 'Content-Type': 'application/json' },
      {},
      (resp) => {
        if (resp?.success && resp?.data) {
          setProfiles(resp.data);
        }
        setIsLoading(false);
      },
      (err) => {
        console.error('Failed to load profiles:', err);
        setIsLoading(false);
      }
    );
  };

  const handleEdit = (profile) => {
    axiosPost(
      'country-profiles/get',
      { 'Content-Type': 'application/json' },
      { id: profile.id },
      (resp) => {
        if (resp?.success && resp?.data) {
          const data = resp.data;
          setEditorData({
            id: data.id,
            country_id: data.country_id,
            title: data.title || `${data.country_name} Travel Guide`,
            excerpt: data.excerpt || '',
            image: data.featured_image_url || data.country_image || '',
            status: data.status || 'draft',
            blocks: data.blocks || []
          });
          setIsEditorOpen(true);
        }
      },
      (err) => console.error('Failed to load profile:', err)
    );
  };

  const handleCreateNew = () => {
    if (!selectedCountryId) {
      alert('Please select a country first');
      return;
    }

    const country = countries.find(c => c.id === parseInt(selectedCountryId));
    
    axiosPost(
      'country-profiles/get',
      { 'Content-Type': 'application/json' },
      { country_id: parseInt(selectedCountryId) },
      (resp) => {
        if (resp?.success && resp?.data) {
          const data = resp.data;
          setEditorData({
            id: data.id,
            country_id: data.country_id,
            title: data.title || `${data.country_name} Travel Guide`,
            excerpt: data.excerpt || '',
            image: data.featured_image_url || '',
            status: data.status || 'draft',
            blocks: data.blocks || []
          });
        } else {
          setEditorData({
            id: null,
            country_id: parseInt(selectedCountryId),
            title: `${country?.name || 'Country'} Travel Guide`,
            excerpt: '',
            image: country?.image || '',
            status: 'draft',
            blocks: []
          });
        }
        setIsEditorOpen(true);
      },
      (err) => {
        setEditorData({
          id: null,
          country_id: parseInt(selectedCountryId),
          title: `${country?.name || 'Country'} Travel Guide`,
          excerpt: '',
          image: country?.image || '',
          status: 'draft',
          blocks: []
        });
        setIsEditorOpen(true);
      }
    );
  };

  const handleEditorSave = (data) => {
    const payload = {
      id: data.id || 0,
      country_id: editorData?.country_id || 0,
      title: data.title,
      excerpt: data.excerpt,
      image: data.image,
      status: data.status || 'published',
      blocks: data.blocks || []
    };

    axiosPost(
      'country-profiles/update',
      { 'Content-Type': 'application/json' },
      payload,
      (resp) => {
        if (resp?.success) {
          setIsEditorOpen(false);
          setEditorData(null);
          loadProfiles();
          alert('Country profile saved successfully!');
        } else {
          console.error('Save failed:', resp?.response);
          alert('Failed to save: ' + (resp?.response || 'Unknown error'));
        }
      },
      (err) => console.error('Save error:', err)
    );
  };

  const handleEditorClose = () => {
    setIsEditorOpen(false);
    setEditorData(null);
  };

  const countriesWithoutProfiles = countries.filter(
    c => !profiles.some(p => parseInt(p.country_id) === parseInt(c.id))
  );

  if (isEditorOpen && editorData) {
    return (
      <ArticleEditor
        initialData={editorData}
        onSave={handleEditorSave}
        onClose={handleEditorClose}
        mode="site-page"
      />
    );
  }

  return (
    <div>
      <div className="DashboardPanel">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h1 className="DashboardTitle">Country Profiles</h1>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <select
              value={selectedCountryId}
              onChange={(e) => setSelectedCountryId(e.target.value)}
              className="DashboardFormInput"
              style={{ width: 'auto', minWidth: '200px' }}
            >
              <option value="">Select a country...</option>
              {countriesWithoutProfiles.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <button className="DashboardButton" onClick={handleCreateNew}>
              <FaPlus style={{ marginRight: '5px' }} /> Create Profile
            </button>
          </div>
        </div>

        <p style={{ marginBottom: '20px', color: '#666' }}>
          Manage editable country profile pages. These replace the default country info on the public profile page.
        </p>

        {isLoading ? (
          <div>Loading...</div>
        ) : (
          <table className="DashboardTable">
            <thead>
              <tr>
                <th>Country</th>
                <th>Title</th>
                <th>Status</th>
                <th>Last Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {profiles.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{ textAlign: 'center', padding: '20px' }}>
                    No country profiles yet. Select a country and create one.
                  </td>
                </tr>
              ) : (
                profiles.map((profile) => (
                  <tr key={profile.id}>
                    <td>{profile.country_name}</td>
                    <td>{profile.title || 'Untitled'}</td>
                    <td>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        background: profile.status === 'published' ? '#e8f5e9' : '#fff3e0',
                        color: profile.status === 'published' ? '#2e7d32' : '#e65100'
                      }}>
                        {profile.status}
                      </span>
                    </td>
                    <td>{profile.updated_at ? new Date(profile.updated_at * 1000).toLocaleDateString() : '-'}</td>
                    <td>
                      <button
                        onClick={() => handleEdit(profile)}
                        style={{ background: 'none', cursor: 'pointer', marginRight: '10px' }}
                        title="Edit Profile"
                      >
                        <FaEdit color="#1976d2" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
